import { v4 as uuidv4 } from 'uuid';
import { bulkSubscribe, modelState, ModelStateList, modelStateListSubscriptionManager } from '@/utils/modelState';
import type { ReactNode } from 'react';

export interface EditVoucherModel {
    voucherId: string;
    code: string;
    visibilityExpressionJson?: string | null;
    validFrom: string | null;
    validTo: string | null;
    maxRedemptions: number | null;
}

export namespace QueryBuilder {
    export const Node = Symbol("Node");
    export interface Node {
        [Node]: boolean;
        id: string;
        version: modelState<number>;
        dispose(): void;
    }

    export const GroupNode = Symbol("GroupNode");
    export interface GroupNode extends Node {
        [GroupNode]: boolean;
        condition: modelState<"and" | "or">;
        children: ModelStateList<Node>;
    }

    export const ConditionNode = Symbol("ConditionNode");
    export interface ConditionNode extends Node {
        [ConditionNode]: boolean;
        childNode: modelState<ConditionNodeChild | null>;
    }

    export const ConditionNodeChild = Symbol("ConditionNodeChild");
    export interface ConditionNodeChild extends Node {
        [ConditionNodeChild]: boolean;
    }

    export function isGroupNode(node: Node): node is GroupNode {
        return (node as GroupNode)[GroupNode] === true
    }

    export function isConditionNode(node: Node): node is ConditionNode {
        return (node as ConditionNode)[ConditionNode] === true
    }

    export function createGroupNode(conditionRegistry: ConditionRegistryEntry[], skipHydration: boolean = false): GroupNode {

        const node = {
            [Node]: true,
            [GroupNode]: true,
            id: uuidv4(),
            version: modelState(0),
            condition: modelState<"and" | "or">("and"),
            children: new ModelStateList<Node>([]),
            dispose: () => { }
        };

        if (!skipHydration) {
            hydrateGroupNode(node, conditionRegistry);
        }

        return node;
    }

    export function hydrateGroupNode(node: GroupNode, conditionRegistry: ConditionRegistryEntry[], recursive: boolean = false) {

        const unsubscribeCondition = node.condition.subscribe(() => {
            node.version.setValue(node.version.value + 1);
        });

        const unsubscribeList = bulkSubscribe(
            node.children,
            [
                c => c.version
            ],
            () => {
                node.version.setValue(node.version.value + 1);
            });

        const oldDispose = node.dispose;

        node.dispose = () => {
            unsubscribeCondition();
            unsubscribeList();

            node.children.toArray().forEach(c => c.dispose());

            oldDispose();
        }

        if (recursive) {
            node.children.toArray().forEach(c => hydrateNode(c, conditionRegistry, true));
        }
    }

    function hydrateNode(node: Node, conditionRegistry: ConditionRegistryEntry[], recursive: boolean = false) {
        if (isGroupNode(node)) {
            hydrateGroupNode(node, conditionRegistry, recursive);
        } else if (isConditionNode(node)) {
            hydrateConditionNode(node, conditionRegistry, recursive);
        }
    }

    export function createConditionNode(conditionRegistry: ConditionRegistryEntry[], skipHydration: boolean = false): ConditionNode {
        const conditionNode = {
            [Node]: true,
            [ConditionNode]: true,
            id: uuidv4(),
            version: modelState(0),
            childNode: modelState<ConditionNodeChild>(null),
            dispose: () => {
                conditionNode.childNode.value?.dispose();
            }
        };

        if (!skipHydration) {
            hydrateConditionNode(conditionNode, conditionRegistry);
        }

        return conditionNode;
    }

    function hydrateConditionNode(node: ConditionNode, conditionRegistry: ConditionRegistryEntry[], recursive: boolean = false) {

        let oldSubscription: () => void = null;

        function subscribeChildNode() {
            if (node.childNode.value) {
                oldSubscription = node.childNode.value.version.subscribe(() => {
                    node.version.setValue(node.version.value + 1);
                });
            }
        }

        subscribeChildNode();

        const unsubscribeChildNode = node.childNode.subscribe(() => {
            oldSubscription?.();

            subscribeChildNode();

            node.version.setValue(node.version.value + 1);
        });

        const oldDispose = node.dispose

        node.dispose = () => {
            unsubscribeChildNode();
            oldSubscription?.();

            oldDispose();
        };

        if (recursive) {
            hydrateConditionChildNode(node.childNode.value, conditionRegistry, recursive);
        }
    }

    function hydrateConditionChildNode(childNode: ConditionNodeChild | null, conditionRegistry: ConditionRegistryEntry[], recursive: boolean = false) {
        if (!childNode) {
            return;
        }

        const registryEntry = conditionRegistry.find(r => r.isCondition(childNode));

        if (!registryEntry) {
            throw new Error("Unknown condition child node type");
        }

        registryEntry.hydrate(childNode, recursive);
    }

    export function getRootNodeDtoModel(node: GroupNode, conditionRegistry: ConditionRegistryEntry[]): GroupNodeDto {
        const groupNodeDto = getGroupNodeDtoModel(node, conditionRegistry);
        return groupNodeDto.Value;
    }

    function getNodeDtoModel(node: Node, conditionRegistry: ConditionRegistryEntry[]) {
        if (isGroupNode(node)) {
            return getGroupNodeDtoModel(node, conditionRegistry);
        } else if (isConditionNode(node)) {
            return getConditionNodeDtoModel(node, conditionRegistry);
        }

        throw new Error("Unknown node type");
    }

    function getGroupNodeDtoModel(node: GroupNode, conditionRegistry: ConditionRegistryEntry[]): NodeDtoWrapperT<GroupNodeDto> {
        return {
            Type: "GroupNode",
            Value: {
                NodeId: node.id,
                Condition: node.condition.value,
                Nodes: node.children.toArray().map(n => getNodeDtoModel(n, conditionRegistry))
            }
        };
    }

    function getConditionNodeDtoModel(node: ConditionNode, conditionRegistry: ConditionRegistryEntry[]): NodeDtoWrapperT<ConditionNodeDto> {
        return {
            Type: "ConditionNode",
            Value: {
                NodeId: node.id,
                ChildNode: getConditionChildNodeDtoModel(node.childNode.value, conditionRegistry)
            }
        };
    }

    function getConditionChildNodeDtoModel(childNode: ConditionNodeChild, conditionRegistry: ConditionRegistryEntry[]): ConditionChildNodeDtoWrapper {
        if (!childNode) {
            return null;
        }

        const registryEntry = conditionRegistry.find(r => r.isCondition(childNode));

        if (!registryEntry) {
            throw new Error("Unknown condition child node type");
        }

        return registryEntry.serialize(childNode);
    }

    export interface NodeDto {
        NodeId: string;
    }

    export interface ConditionChildNodeDtoWrapper {
        Type: string;
        Value: NodeDto;
    }

    export interface GroupNodeDto extends NodeDto {
        Condition: "and" | "or";
        Nodes: NodeDtoWrapper[];
    }

    export interface ConditionNodeDto extends NodeDto {
        ChildNode: ConditionChildNodeDtoWrapper;
    }

    export interface NodeDtoWrapper {
        Type: string;
        Value: NodeDto;
    }

    export interface NodeDtoWrapperT<T extends NodeDto> extends NodeDtoWrapper {
        Value: T;
    }

    export function getConditionTypes(conditionRegistry: ConditionRegistryEntry[]): Record<string, string> {
        return conditionRegistry.reduce((acc, r) => {
            acc[r.key] = r.name;
            return acc;
        }, {} as Record<string, string>);
    }

    export function getConditionTypeKey(node: ConditionNode, conditionRegistry: ConditionRegistryEntry[],): string {
        const registryEntry = conditionRegistry.find(r => r.isCondition(node.childNode.value));

        if (!registryEntry) {
            return null;
        }

        return registryEntry.key;
    }

    export function applyConditionNodeChild(node: ConditionNode, type: string | null, conditionRegistry: ConditionRegistryEntry[],) {
        node.childNode.value?.dispose();

        const registryEntry = conditionRegistry.find(r => r.key === type);

        if (registryEntry) {
            node.childNode.setValue(registryEntry.create());
        } else {
            node.childNode.setValue(null);
        }
    }

    export function deserialize(inputJson: string, conditionRegistry: ConditionRegistryEntry[]): GroupNode {
        const rootNodeDto = JSON.parse(inputJson) as GroupNodeDto;

        const rootNode = deserializeGroupNode(rootNodeDto, conditionRegistry);

        hydrateGroupNode(rootNode, conditionRegistry, true);

        return rootNode;
    }

    export function deserializeGroupNode(rootNodeDto: QueryBuilder.GroupNodeDto, conditionRegistry: ConditionRegistryEntry[]): QueryBuilder.GroupNode {
        const groupNode = createGroupNode(conditionRegistry, true);

        groupNode.id = rootNodeDto.NodeId;

        groupNode.condition.setValue(rootNodeDto.Condition);
        groupNode.condition.commit();

        const children = rootNodeDto.Nodes.map(n => deserializeNode(n, conditionRegistry));

        groupNode.children.clear();

        children.forEach(c => groupNode.children.add(c));

        groupNode.children.commitChange();

        return groupNode;
    }

    function deserializeNode(nodeDto: QueryBuilder.NodeDtoWrapper, conditionRegistry: ConditionRegistryEntry[]): QueryBuilder.Node {
        if (nodeDto.Type === "GroupNode") {
            return deserializeGroupNode(nodeDto.Value as GroupNodeDto, conditionRegistry);
        } else if (nodeDto.Type === "ConditionNode") {
            return deserializeConditionNode(nodeDto.Value as ConditionNodeDto, conditionRegistry);
        }

        throw new Error("Unknown node type");
    }

    function deserializeConditionNode(conditionNodeDto: QueryBuilder.ConditionNodeDto, conditionRegistry: ConditionRegistryEntry[]): QueryBuilder.ConditionNode {
        const conditionNode = createConditionNode(conditionRegistry, true);

        conditionNode.id = conditionNodeDto.NodeId;

        if (conditionNodeDto.ChildNode) {
            conditionNode.childNode.setValue(deserializeConditionChildNode(conditionNodeDto.ChildNode, conditionRegistry));
            conditionNode.childNode.commit();
        }

        return conditionNode;
    }

    function deserializeConditionChildNode(childNodeDto: QueryBuilder.ConditionChildNodeDtoWrapper, conditionRegistry: ConditionRegistryEntry[]): QueryBuilder.ConditionNodeChild {
        const registryEntry = conditionRegistry.find(r => r.key === childNodeDto.Type);

        if (!registryEntry) {
            throw new Error("Unknown condition child node type");
        }

        return registryEntry.deserialize(childNodeDto.Value);
    }

    export interface ConditionRegistryEntry {
        key: string;
        name: string;
        isCondition: (node: ConditionNodeChild) => boolean;
        create: () => ConditionNodeChild;
        hydrate: (node: ConditionNodeChild, recursive: boolean) => void;
        serialize: (node: ConditionNodeChild) => ConditionChildNodeDtoWrapper;
        deserialize: (dto: NodeDto) => ConditionNodeChild;
        renderComponent(value: ConditionNodeChild): ReactNode;
    }

    export function renderConditionChildNodeComponent(node: ConditionNode, conditionRegistry: ConditionRegistryEntry[]): ReactNode | null {
        if (!node.childNode.value) {
            return null;
        }

        const registryEntry = conditionRegistry.find(r => r.isCondition(node.childNode.value));

        if (!registryEntry) {
            return null;
        }

        return registryEntry.renderComponent(node.childNode.value);
    }
}
